import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PiggyBank } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { money } from './DeskForms';
import { DeskFeeSettings, InterestForm } from './EarningsForms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-desk/admin`;
export const SOURCES = [
  { k: 'mint_fee', label: 'Mint fees', color: '#10b981' },
  { k: 'redeem_fee', label: 'Redeem fees', color: '#f59e0b' },
  { k: 'swap_fee', label: 'Swap fees', color: '#06b6d4' },
  { k: 'cashout_fee', label: 'Credit cash-out fees', color: '#f472b6' },
  { k: 'subscription', label: 'Tier 2 subscriptions', color: '#facc15' },
  { k: 'reserve_interest', label: 'Reserve interest', color: '#a78bfa' },
];

const Stat = ({ k, v, testid, color }) => (
  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
    <p className="text-xs text-slate-400">{k}</p>
    <p className="text-lg font-bold" style={{ color: color || '#fff' }} data-testid={testid}>{v}</p>
  </div>
);

const Recent = ({ rows }) => (
  <div className="overflow-x-auto" data-testid="earnings-recent">
    {!rows.length ? <p className="text-xs text-slate-500">No earnings yet. They appear here as desk requests and swaps complete.</p> : (
      <table className="w-full text-sm text-slate-300">
        <thead><tr className="text-left text-xs text-slate-500"><th>When</th><th>Source</th><th>Amount</th><th>Detail</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-t border-slate-700"><td className="py-1.5 text-xs">{new Date(r.created_at).toLocaleString()}</td>
            <td className="text-xs">{SOURCES.find(s => s.k === r.source)?.label}</td><td className="text-emerald-400">{money(r.amount_usd)}</td>
            <td className="text-xs text-slate-400">{r.note} {r.email ? `· ${r.email}` : ''}</td></tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

export const EarningsDashboard = ({ token }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/earnings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setD(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-slate-400 text-sm">Loading earnings…</p>;

  return (
    <div className="space-y-6" data-testid="earnings-dashboard">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><PiggyBank className="w-5 h-5 text-emerald-400" /> Earnings</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat k="All time" v={money(d.total_usd)} testid="earnings-total" color="#34d399" />
          <Stat k="This month" v={money(d.this_month_usd)} testid="earnings-month" />
          {SOURCES.map(s => <Stat key={s.k} k={s.label} v={money(d.by_source[s.k])} testid={`earnings-${s.k}`} color={s.color} />)}
        </div>
        <div className="h-64" data-testid="earnings-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.daily}>
              <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {SOURCES.map(s => <Bar key={s.k} dataKey={s.k} name={s.label} stackId="a" fill={s.color} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <DeskFeeSettings token={token} settings={d.settings} onSaved={load} />
        <InterestForm token={token} onSaved={load} />
      </div>
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-white font-semibold mb-3">Recent earnings</h3>
        <Recent rows={d.recent} />
      </div>
    </div>
  );
};
